import React, { useEffect, useState, useCallback } from "react";
import { Zap, DollarSign, Loader2, ChevronRight, X as XIcon, Check } from "lucide-react";
import { COLOR } from "./pillars/_cards";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const isoDay = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const shortTime = (iso) =>
  iso
    ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : "—";

/**
 * ChargingLedgerCard — auto-detected charging sessions + $/mile tracker.
 *
 * Sessions are opened/closed by the backend on every Fleet API pull
 * (see backend/charging_ledger.py). This card:
 *   • Shows a rolling 30-day summary — total kWh, total cost, $/mile
 *   • Lets the owner tap any session to enter its cost or override kWh
 *   • Recomputes summary on save
 */
export const ChargingLedgerCard = ({ vehicle }) => {
  const vin = vehicle?.vin || null;
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(null); // session_id or null

  const load = useCallback(async () => {
    if (!vin) return;
    setLoading(true);
    try {
      const [sRes, cRes] = await Promise.all([
        fetch(`${API_BASE}/api/charging/${encodeURIComponent(vin)}/sessions?limit=20`),
        fetch(`${API_BASE}/api/charging/${encodeURIComponent(vin)}/summary?window_days=30`),
      ]);
      if (sRes.ok) {
        const d = await sRes.json();
        setSessions(d.sessions || []);
      }
      if (cRes.ok) {
        const d = await cRes.json();
        setSummary(d);
      }
    } catch (_e) {
      /* offline */
    } finally {
      setLoading(false);
    }
  }, [vin]);

  useEffect(() => {
    setSessions([]);
    setSummary(null);
    setExpanded(false);
    setEditing(null);
    if (vin) load();
  }, [vin, load]);

  const saveSession = async (sid, body) => {
    if (!vin) return;
    try {
      const r = await fetch(
        `${API_BASE}/api/charging/${encodeURIComponent(vin)}/session/${encodeURIComponent(sid)}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (r.ok) {
        setEditing(null);
        await load();
      }
    } catch (_e) {
      /* offline */
    }
  };

  const closedCount = sessions.filter((s) => s.status === "closed").length;
  const openSession = sessions.find((s) => s.status === "open");
  const priceHint =
    summary?.cost_per_kwh_usd || summary?.default_price_per_kwh_usd || 0.14;

  return (
    <div
      className="rounded-xl border p-3"
      style={{
        borderColor: "rgba(255,176,32,0.28)",
        background: "linear-gradient(135deg, rgba(255,176,32,0.04), rgba(255,255,255,0.02))",
      }}
      data-testid="care-charging-card"
    >
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-md flex items-center justify-center border shrink-0"
          style={{ background: "rgba(255,176,32,0.14)", borderColor: "rgba(255,176,32,0.45)" }}
        >
          <Zap className="w-4 h-4" style={{ color: COLOR.amber }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Charging ledger · trailing 30 days
          </div>
          <div className="text-[13px] text-white font-semibold leading-tight mt-0.5" data-testid="care-charging-headline">
            {summary?.cost_per_mi_usd != null
              ? `$${summary.cost_per_mi_usd.toFixed(3)} / mile driven`
              : closedCount === 0
              ? "No sessions logged yet"
              : "Tap a session to enter its cost"}
          </div>
          <div className="font-mono text-[10px] text-zinc-400 leading-tight mt-0.5">
            {summary?.session_count ?? 0} session{summary?.session_count === 1 ? "" : "s"}
            {" · "}
            {summary?.total_kwh?.toFixed(1) ?? "0.0"} kWh added
            {summary?.total_cost_usd > 0 && (
              <> · ${summary.total_cost_usd.toFixed(2)} spent</>
            )}
            {openSession && (
              <span
                className="ml-1.5 inline-flex items-center gap-0.5 rounded px-1 py-[1px] font-mono text-[9px] uppercase"
                style={{ background: `${COLOR.green}18`, color: COLOR.green }}
                data-testid="care-charging-open-badge"
              >
                <span className="w-1 h-1 rounded-full bg-current animate-pulse" />
                charging now
              </span>
            )}
          </div>
        </div>
        {sessions.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid="care-charging-toggle"
            className="shrink-0 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-colors"
          >
            {expanded ? "Hide" : `${sessions.length} log`}
          </button>
        )}
      </div>

      {expanded && (
        <div
          className="mt-3 border-t border-white/5 pt-2 space-y-2 max-h-80 overflow-y-auto pr-1"
          data-testid="care-charging-list"
        >
          {sessions.length === 0 && (
            <div className="text-[11px] text-zinc-500 italic py-2">
              Charging sessions will appear here after your next plug-in.
            </div>
          )}
          {sessions.map((s, i) => (
            <SessionRow
              key={s.session_id || i}
              session={s}
              priceHint={priceHint}
              isEditing={editing === s.session_id}
              onEdit={() => setEditing(editing === s.session_id ? null : s.session_id)}
              onSave={(body) => saveSession(s.session_id, body)}
              testid={`care-charging-session-${i}`}
            />
          ))}
        </div>
      )}
      {loading && sessions.length === 0 && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500">
          <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.2} />
          Loading sessions…
        </div>
      )}
    </div>
  );
};

const SessionRow = ({ session: s, priceHint, isEditing, onEdit, onSave, testid }) => {
  const kwh = s.kwh_added;
  const cost = s.cost_usd;
  const [kwhInp, setKwhInp] = useState(kwh != null ? String(kwh) : "");
  const [priceInp, setPriceInp] = useState(
    s.price_per_kwh_usd != null ? String(s.price_per_kwh_usd) : String(priceHint || 0.14),
  );
  const [noteInp, setNoteInp] = useState(s.note || "");

  const submit = (e) => {
    e.preventDefault();
    const kwhNum = parseFloat(kwhInp);
    const priceNum = parseFloat(priceInp);
    const body = {};
    if (!isNaN(kwhNum)) body.kwh_added = kwhNum;
    if (!isNaN(priceNum)) body.price_per_kwh_usd = priceNum;
    if (noteInp.trim()) body.note = noteInp.trim();
    onSave(body);
  };

  return (
    <div
      className="rounded-md border p-2"
      style={{ borderColor: "rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}
      data-testid={testid}
    >
      <button
        type="button"
        onClick={onEdit}
        className="w-full flex items-center gap-2 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="text-[11px] text-white font-medium leading-snug">
            {isoDay(s.start_ts)} · {shortTime(s.start_ts)}
            {s.status === "closed" && s.end_ts && (
              <span className="text-zinc-500 font-mono"> → {shortTime(s.end_ts)}</span>
            )}
          </div>
          <div className="font-mono text-[10px] text-zinc-400 mt-0.5">
            {kwh != null ? `${kwh.toFixed(1)} kWh` : "kWh pending"}
            {s.start_level != null && s.end_level != null && (
              <> · {s.start_level}% → {s.end_level}%</>
            )}
            {cost != null && cost > 0 && <> · ${cost.toFixed(2)}</>}
            {s.status === "open" && (
              <span className="ml-1 text-[#39FF14]"> · in progress</span>
            )}
          </div>
        </div>
        <ChevronRight
          className={`w-3.5 h-3.5 text-zinc-500 shrink-0 transition-transform ${isEditing ? "rotate-90" : ""}`}
        />
      </button>

      {isEditing && (
        <form onSubmit={submit} className="mt-2 pt-2 border-t border-white/5 space-y-2" data-testid={`${testid}-editor`}>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                kWh added
              </span>
              <input
                type="number"
                step="0.1"
                min="0"
                max="200"
                value={kwhInp}
                onChange={(e) => setKwhInp(e.target.value)}
                placeholder={kwh != null ? `${kwh}` : "e.g. 32.5"}
                data-testid={`${testid}-kwh`}
                className="mt-0.5 w-full rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] text-white focus:outline-none focus:border-[#FFB020]/60 focus:ring-1 focus:ring-[#FFB020]/30"
              />
            </label>
            <label className="block">
              <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                $/kWh
              </span>
              <input
                type="number"
                step="0.001"
                min="0"
                max="2"
                value={priceInp}
                onChange={(e) => setPriceInp(e.target.value)}
                data-testid={`${testid}-price`}
                className="mt-0.5 w-full rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] text-white focus:outline-none focus:border-[#FFB020]/60 focus:ring-1 focus:ring-[#FFB020]/30"
              />
            </label>
          </div>
          <label className="block">
            <span className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
              Note <span className="text-zinc-600 normal-case font-sans">(location, network)</span>
            </span>
            <input
              type="text"
              value={noteInp}
              maxLength={120}
              onChange={(e) => setNoteInp(e.target.value)}
              placeholder="e.g. home L2 · SC Buc-ee's I-10"
              data-testid={`${testid}-note`}
              className="mt-0.5 w-full rounded-md border border-white/10 bg-black/60 px-2 py-1 text-[11px] text-white placeholder-zinc-600 focus:outline-none focus:border-[#FFB020]/60 focus:ring-1 focus:ring-[#FFB020]/30"
            />
          </label>
          <div className="flex items-center gap-2 justify-end">
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-300 hover:text-white transition-colors"
              data-testid={`${testid}-cancel`}
            >
              <XIcon className="w-3 h-3" strokeWidth={2.2} />
              Cancel
            </button>
            <button
              type="submit"
              data-testid={`${testid}-save`}
              className="inline-flex items-center gap-1 rounded-md brand-gradient text-black px-2 py-1 text-[10px] font-mono uppercase tracking-wider font-semibold hover:opacity-90"
            >
              <Check className="w-3 h-3" strokeWidth={2.4} />
              Save
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
